/**
 * BulkImportModal - Industrial-styled CSV import interface
 *
 * Features file upload, preview, progress tracking, and error reporting
 * with the command centre aesthetic.
 */

import { useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Group,
  Modal,
  Progress,
  ScrollArea,
  Stack,
  Table,
  Text,
  Transition,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconUpload, IconDownload } from '@/components/icons';

interface ImportError {
  row: number;
  message: string;
}

interface ImportResult {
  imported: number;
  errors: ImportError[];
}

interface BulkImportModalProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  entityType: 'equipment' | 'users' | 'departments' | 'locations';
  expectedColumns: string[];
  onImport: (file: File) => Promise<ImportResult>;
  sampleData?: Record<string, string>[];
}

/** Parse CSV string into array of objects */
function parseCSV(csv: string, expectedColumns: string[]): { data: Record<string, string>[]; errors: string[] } {
  const lines = csv.trim().split('\n');
  const errors: string[] = [];
  const data: Record<string, string>[] = [];

  if (lines.length < 2) {
    errors.push('File must contain a header row and at least one data row');
    return { data, errors };
  }

  const headers = (lines[0] ?? '').split(',').map((h) => h.trim().toLowerCase());

  // Validate headers
  const missingColumns = expectedColumns.filter(
    (col) => !headers.includes(col.toLowerCase())
  );
  if (missingColumns.length > 0) {
    errors.push(`Missing required columns: ${missingColumns.join(', ')}`);
  }

  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const values = (lines[i] ?? '').split(',').map((v) => v.trim());
    if (values.length !== headers.length) {
      errors.push(`Row ${i + 1}: Column count mismatch (expected ${headers.length}, got ${values.length})`);
      continue;
    }

    const row: Record<string, string> = {};
    headers.forEach((header, idx) => {
      row[header] = values[idx] ?? '';
    });
    data.push(row);
  }

  return { data, errors };
}

/** Generate sample CSV content */
function generateSampleCSV(columns: string[], sampleData?: Record<string, string>[]): string {
  const header = columns.join(',');
  if (sampleData && sampleData.length > 0) {
    const rows = sampleData.map((row) =>
      columns.map((col) => row[col.toLowerCase()] || '').join(',')
    );
    return [header, ...rows].join('\n');
  }
  return header + '\n' + columns.map(() => '...').join(',');
}

export function BulkImportModal({
  opened,
  onClose,
  title,
  entityType,
  expectedColumns,
  onImport,
  sampleData,
}: BulkImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Record<string, string>[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setFile(null);
    setPreview([]);
    setParseErrors([]);
    setImporting(false);
    setProgress(0);
    setResult(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (!selectedFile) return;

      if (!selectedFile.name.endsWith('.csv')) {
        notifications.show({
          title: 'Invalid File',
          message: 'Please select a CSV file',
          color: 'red',
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        const { data, errors } = parseCSV(content, expectedColumns);
        setFile(selectedFile);
        setPreview(data.slice(0, 5)); // Preview first 5 rows
        setParseErrors(errors);
        setResult(null);
      };
      reader.readAsText(selectedFile);
    },
    [expectedColumns]
  );

  const handleImport = useCallback(async () => {
    if (!file) return;

    setImporting(true);
    setProgress(0);

    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setProgress((p) => Math.min(p + 10, 90));
    }, 200);

    try {
      const importResult = await onImport(file);
      clearInterval(progressInterval);
      setProgress(100);
      setResult(importResult);

      if (importResult.errors.length === 0) {
        notifications.show({
          title: 'Import Complete',
          message: `Successfully imported ${importResult.imported} ${entityType}`,
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Import Completed with Errors',
          message: `Imported ${importResult.imported} ${entityType}, ${importResult.errors.length} errors`,
          color: 'yellow',
        });
      }
    } catch (error) {
      clearInterval(progressInterval);
      notifications.show({
        title: 'Import Failed',
        message: 'An error occurred during import',
        color: 'red',
      });
    } finally {
      setImporting(false);
    }
  }, [file, onImport, entityType]);

  const handleDownloadTemplate = useCallback(() => {
    const csv = generateSampleCSV(expectedColumns, sampleData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entityType}-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [expectedColumns, sampleData, entityType]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={title}
      size="lg"
      styles={{
        header: {
          backgroundColor: 'var(--bg-card)',
          borderBottom: '1px solid var(--border-colour)',
        },
        body: {
          backgroundColor: 'var(--bg-base)',
        },
        title: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#3B82F6',
        },
        content: {
          backgroundColor: 'var(--bg-base)',
          border: '1px solid var(--border-colour)',
        },
      }}
    >
      <Stack gap="lg">
        {/* Header info */}
        <Box
          style={{
            background: 'linear-gradient(90deg, var(--blue-bg) 0%, transparent 100%)',
            borderLeft: '3px solid #3B82F6',
            padding: '0.75rem 1rem',
            borderRadius: '0 4px 4px 0',
          }}
        >
          <Text
            size="sm"
            style={{
              fontFamily: '"Inter", sans-serif',
              color: 'var(--text-muted)',
            }}
          >
            Expected columns:{' '}
            <Text
              span
              ff="monospace"
              c="#3B82F6"
              fw={500}
            >
              {expectedColumns.join(', ')}
            </Text>
          </Text>
        </Box>

        {/* File input area */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {!file ? (
          <Box
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: '2px dashed var(--border-colour)',
              borderRadius: 6,
              padding: '2rem',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 150ms ease',
              background: 'var(--bg-card)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#3B82F6';
              e.currentTarget.style.background = 'var(--bg-base)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-colour)';
              e.currentTarget.style.background = 'var(--bg-card)';
            }}
          >
            <IconUpload
              size={32}
              style={{ color: 'var(--text-muted)', marginBottom: 8 }}
            />
            <Text
              style={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.875rem',
                color: 'var(--text-secondary)',
              }}
            >
              Click to select CSV file or drag and drop
            </Text>
            <Text
              size="xs"
              c="dimmed"
              mt={4}
            >
              Maximum file size: 5MB
            </Text>
          </Box>
        ) : (
          <Box
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-colour)',
              borderRadius: 6,
              padding: '1rem',
            }}
          >
            <Group justify="space-between" mb="md">
              <Group gap="sm">
                <Box
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: parseErrors.length > 0 ? '#F59E0B' : '#22C55E',
                    boxShadow: `0 0 8px ${parseErrors.length > 0 ? 'rgba(245, 158, 11, 0.6)' : 'rgba(34, 197, 94, 0.6)'}`,
                  }}
                />
                <Text
                  ff="monospace"
                  size="sm"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {file.name}
                </Text>
              </Group>
              <Button
                variant="subtle"
                size="xs"
                onClick={reset}
                color="gray"
              >
                Clear
              </Button>
            </Group>

            {/* Parse errors */}
            {parseErrors.length > 0 && (
              <Box
                mb="md"
                style={{
                  background: 'var(--red-bg)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: 4,
                  padding: '0.75rem',
                }}
              >
                <Text
                  size="xs"
                  fw={600}
                  c="#EF4444"
                  mb={4}
                  style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                  Parse Warnings
                </Text>
                {parseErrors.map((error, idx) => (
                  <Text key={idx} size="xs" c="#EF4444">
                    {error}
                  </Text>
                ))}
              </Box>
            )}

            {/* Preview table */}
            {preview.length > 0 && (
              <>
                <Text
                  size="xs"
                  c="dimmed"
                  mb="xs"
                  style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                  Preview (first {preview.length} rows)
                </Text>
                <ScrollArea>
                  <Table
                    withTableBorder
                    withColumnBorders
                    styles={{
                      table: { backgroundColor: 'var(--bg-base)' },
                      th: {
                        backgroundColor: 'var(--bg-card)',
                        color: 'var(--text-muted)',
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderColor: 'var(--border-colour)',
                      },
                      td: {
                        fontSize: '0.8rem',
                        fontFamily: 'monospace',
                        borderColor: 'var(--border-colour)',
                        color: 'var(--text-primary)',
                      },
                    }}
                  >
                    <Table.Thead>
                      <Table.Tr>
                        {expectedColumns.map((col) => (
                          <Table.Th key={col}>{col}</Table.Th>
                        ))}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {preview.map((row, idx) => (
                        <Table.Tr key={idx}>
                          {expectedColumns.map((col) => (
                            <Table.Td key={col}>{row[col.toLowerCase()] || '—'}</Table.Td>
                          ))}
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              </>
            )}
          </Box>
        )}

        {/* Progress bar */}
        <Transition mounted={importing} transition="fade" duration={200}>
          {(styles) => (
            <Box style={styles}>
              <Text size="xs" c="dimmed" mb={4}>
                Importing...
              </Text>
              <Progress
                value={progress}
                size="sm"
                color="blue"
                styles={{
                  root: { backgroundColor: 'var(--bg-card)' },
                  section: {
                    boxShadow: '0 0 10px rgba(59, 130, 246, 0.5)',
                  },
                }}
              />
            </Box>
          )}
        </Transition>

        {/* Import result */}
        {result && (
          <Box
            style={{
              background: result.errors.length > 0
                ? 'var(--amber-bg)'
                : 'var(--green-bg)',
              border: `1px solid ${result.errors.length > 0 ? 'rgba(245, 158, 11, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`,
              borderRadius: 4,
              padding: '1rem',
            }}
          >
            <Group gap="lg">
              <Box>
                <Text
                  ff="monospace"
                  size="xl"
                  fw={600}
                  c="#22C55E"
                  style={{ textShadow: '0 0 20px rgba(34, 197, 94, 0.5)' }}
                >
                  {result.imported}
                </Text>
                <Text size="xs" c="dimmed">
                  Imported
                </Text>
              </Box>
              {result.errors.length > 0 && (
                <Box>
                  <Text
                    ff="monospace"
                    size="xl"
                    fw={600}
                    c="#EF4444"
                    style={{ textShadow: '0 0 20px rgba(239, 68, 68, 0.5)' }}
                  >
                    {result.errors.length}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Errors
                  </Text>
                </Box>
              )}
            </Group>

            {result.errors.length > 0 && (
              <ScrollArea h={120} mt="md">
                {result.errors.map((err, idx) => (
                  <Text key={idx} size="xs" c="#EF4444" mb={2}>
                    Row {err.row}: {err.message}
                  </Text>
                ))}
              </ScrollArea>
            )}
          </Box>
        )}

        {/* Actions */}
        <Group justify="space-between">
          <Button
            variant="subtle"
            leftSection={<IconDownload size={16} />}
            onClick={handleDownloadTemplate}
            color="gray"
          >
            Download Template
          </Button>
          <Group gap="sm">
            <Button variant="subtle" onClick={handleClose} color="gray">
              {result ? 'Close' : 'Cancel'}
            </Button>
            {!result && (
              <Button
                onClick={handleImport}
                loading={importing}
                disabled={!file || parseErrors.length > 0}
                leftSection={<IconUpload size={16} />}
                color="blue"
              >
                Import
              </Button>
            )}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
